import { useCallback, useMemo, useState } from 'react';
import { useRouter } from 'next/router';

import { reportEvent } from "~/domains/analytics/events";
import { ContestInvitesStatusFilter } from '~/domains/contest/domains/services/enums/contest-invites-status-filter.enum';
import useUpdateContestInviteStatus from '~/domains/contest/hooks/useUpdateContestInviteStatus';
import useContestInvites from '~/domains/contest/hooks/useContestInvites';
import { Routes } from '~/domains/common/constants/routes';
import { URLQueryKeys } from '~/domains/common/types/URLQueryKeys';
import { ContestTab } from '~/domains/contest/domains/entries/hooks/useEntriesTabsProps';

export const useHomeInvites = () => {
  const router = useRouter();
  const [processingInviteId, setProcessingInviteId] = useState<string | null>(null);

  const {
    mutate: declineInvite,
    isLoading: isDeclineInviteLoading,
  } = useUpdateContestInviteStatus({
    onSuccess: () => { setProcessingInviteId(null); },
    onError: () => { setProcessingInviteId(null); },
  });


  const {
    data: invitesData,
    isLoading: isInvitesDataLoading,
    itemsTotal: totalInvites,
  } = useContestInvites({
    status: ContestInvitesStatusFilter.PENDING,
    limit: 1,
  });

  const handleDecline = useCallback(
    (inviteId: string) => {
      reportEvent('Home > Invites > Decline', { count: totalInvites });
      setProcessingInviteId(inviteId);
      declineInvite({
        contestInviteId: inviteId,
        payload: { status: 'DECLINED' },
      });
    },
    [declineInvite]
  );

  const invitesProps = useMemo(() => {
    if (isInvitesDataLoading || !invitesData) return;
    if (totalInvites === 0) return;

    return {
      content: {
        type: 'invites' as const,
        invites: isInvitesDataLoading ? [] : invitesData.pages[0].data.map((invite) => ({
          invite,
          contest: invite.contest,
          isLoading: isDeclineInviteLoading && processingInviteId === invite.id,
        })),
        onInviteDecline: handleDecline,
        onInviteClick: () => reportEvent('Home > Invites > Click', { count: totalInvites }),
      },
      totalCount: totalInvites,
      onViewAllClick: () => {
        reportEvent('Home > Invites > View All', { count: totalInvites });
        router.push(Routes.entries({ [URLQueryKeys.CONTEST_TAB]: ContestTab.INVITES }));
      },
    };
  }, [
    invitesData,
    isInvitesDataLoading,
    isDeclineInviteLoading,
    handleDecline,
    router,
    processingInviteId,
    totalInvites,
  ]);

  return {
    invitesProps,
    isInvitesLoading: isInvitesDataLoading,
  };
};
